import { useCallback, useEffect, useState } from 'react'
import {
  listComments,
  listReplies,
  createComment,
  updateComment,
  deleteComment,
  likeComment,
  unlikeComment,
  saveComment,
  unsaveComment,
} from '../services/comments'
import { standardId } from '../utils/standardRef'

let tempSeq = 0
const tempId = () => `tmp-${Date.now()}-${(tempSeq += 1)}`

function mergeComment(prev, next) {
  return {
    ...prev,
    ...next,
    replyCount: next.replyCount != null ? next.replyCount : prev.replyCount,
  }
}

// Map a picker option (from filterItems) to the anchor shape the card renders.
function optionToAnchor(opt) {
  return {
    item: opt._id,
    standard: opt.standard,
    order: opt.order,
    path: opt.path || [],
    label: opt.label || '',
    pageStart: opt.pageStart,
    pageEnd: opt.pageEnd,
  }
}

// Loads every comment on the item's standard (the whole document's discussion);
// the pane filters/sorts locally. Optimistic create/edit/delete/like/save;
// replies load on demand.
export function useComments(item) {
  const standard = standardId(item?.standard)
  const [comments, setComments] = useState([])
  const [status, setStatus] = useState(standard != null ? 'loading' : 'idle')
  const [repliesByParent, setRepliesByParent] = useState({})

  useEffect(() => {
    if (standard == null) {
      setComments([])
      setRepliesByParent({})
      setStatus('idle')
      return undefined
    }
    let alive = true
    setStatus('loading')
    listComments(standard)
      .then((list) => {
        if (!alive) return
        setComments(list)
        setRepliesByParent({})
        setStatus('ready')
      })
      .catch(() => {
        if (alive) setStatus('error')
      })
    return () => {
      alive = false
    }
  }, [standard])

  // items = picker option objects to tag (defaults to the open item).
  const add = useCallback(
    async ({ title, text, isPublic, isQuestion, items, attachments, guides, topics }) => {
      const id = tempId()
      const anchors = (items || []).map(optionToAnchor)
      const optimistic = {
        _id: id,
        items: anchors,
        title: title || '',
        text,
        isPublic: Boolean(isPublic),
        isQuestion: Boolean(isQuestion),
        parent: null,
        likes: [],
        saves: [],
        attachments: attachments || [],
        guides: guides || [],
        topics: topics || [],
        replyCount: 0,
        createdAt: new Date().toISOString(),
        author: { _id: 'me' },
      }
      setComments((prev) => [optimistic, ...prev])
      try {
        const created = await createComment({
          itemIds: (items || []).map((it) => it._id),
          title: title || '',
          text,
          isPublic,
          isQuestion,
          attachments,
          guides: (guides || []).map((g) => g._id),
          topicIds: (topics || []).map((t) => t._id),
        })
        setComments((prev) => prev.map((c) => (c._id === id ? mergeComment(optimistic, created) : c)))
        return created
      } catch (err) {
        setComments((prev) => prev.filter((c) => c._id !== id))
        throw err
      }
    },
    []
  )

  const update = useCallback(async (id, changes) => {
    let previous
    setComments((prev) => {
      previous = prev.find((c) => c._id === id)
      return prev.map((c) => (c._id === id ? { ...c, ...changes } : c))
    })
    setRepliesByParent((prev) => {
      const next = { ...prev }
      for (const key of Object.keys(next)) {
        next[key] = next[key].map((c) => (c._id === id ? { ...c, ...changes } : c))
      }
      return next
    })
    if (String(id).startsWith('tmp-')) return
    try {
      const updated = await updateComment(id, changes)
      setComments((prev) => prev.map((c) => (c._id === id ? mergeComment(c, updated) : c)))
      setRepliesByParent((prev) => {
        const next = { ...prev }
        for (const key of Object.keys(next)) {
          next[key] = next[key].map((c) => (c._id === id ? mergeComment(c, updated) : c))
        }
        return next
      })
      return updated
    } catch (err) {
      if (previous) {
        setComments((prev) => prev.map((c) => (c._id === id ? previous : c)))
      }
      throw err
    }
  }, [])

  // Re-tag a top-level comment. `items` are picker options (optimistic anchors).
  const updateItems = useCallback(
    async (id, items) => {
      const anchors = (items || []).map(optionToAnchor)
      await update(id, { items: anchors })
      if (String(id).startsWith('tmp-')) return
      try {
        const updated = await updateComment(id, { itemIds: items.map((it) => it._id) })
        setComments((prev) => prev.map((c) => (c._id === id ? mergeComment(c, updated) : c)))
      } catch {
        /* update() already handled optimistic display */
      }
    },
    [update]
  )

  // Re-file a top-level comment under topics. `topics` are { _id, name }.
  const updateTopics = useCallback(async (id, topics) => {
    const next = (topics || []).map((t) => ({ _id: t._id, name: t.name }))
    let previous
    setComments((prev) => {
      previous = prev.find((c) => c._id === id)
      return prev.map((c) => (c._id === id ? { ...c, topics: next } : c))
    })
    if (String(id).startsWith('tmp-')) return
    try {
      const updated = await updateComment(id, { topicIds: next.map((t) => t._id) })
      setComments((prev) => prev.map((c) => (c._id === id ? mergeComment(c, updated) : c)))
    } catch (err) {
      if (previous) setComments((prev) => prev.map((c) => (c._id === id ? previous : c)))
      throw err
    }
  }, [])

  const remove = useCallback(async (id) => {
    let snapshot
    let repliesSnap
    setComments((prev) => {
      snapshot = prev
      return prev.filter((c) => c._id !== id)
    })
    let parentId
    setRepliesByParent((prev) => {
      repliesSnap = prev
      const next = { ...prev }
      delete next[id]
      for (const key of Object.keys(next)) {
        const before = next[key].length
        next[key] = next[key].filter((c) => c._id !== id)
        if (next[key].length !== before) parentId = key
      }
      return next
    })
    if (parentId) {
      setComments((prev) =>
        prev.map((c) =>
          String(c._id) === String(parentId)
            ? { ...c, replyCount: Math.max(0, (c.replyCount || 1) - 1) }
            : c
        )
      )
    }
    if (String(id).startsWith('tmp-')) return
    try {
      await deleteComment(id)
    } catch (err) {
      setComments(snapshot)
      setRepliesByParent(repliesSnap)
      throw err
    }
  }, [])

  // Generic optimistic member toggle for likes / saves.
  const toggleMember = useCallback((field, apiOn, apiOff) => {
    return async (comment, on, userId) => {
      const id = comment._id
      const isMe = (x) => {
        const lid = x && (x._id || x)
        return lid === 'me' || (userId && String(lid) === String(userId))
      }
      const nextList = on
        ? (comment[field] || []).filter((x) => !isMe(x))
        : [...(comment[field] || []), userId || 'me']
      const apply = (c) => (c._id === id ? { ...c, [field]: nextList } : c)
      setComments((prev) => prev.map(apply))
      setRepliesByParent((prev) => {
        const next = { ...prev }
        for (const key of Object.keys(next)) next[key] = next[key].map(apply)
        return next
      })
      if (String(id).startsWith('tmp-')) return
      try {
        const updated = on ? await apiOff(id) : await apiOn(id)
        setComments((prev) => prev.map((c) => (c._id === id ? mergeComment(c, updated) : c)))
        setRepliesByParent((prev) => {
          const next = { ...prev }
          for (const key of Object.keys(next)) {
            next[key] = next[key].map((c) => (c._id === id ? mergeComment(c, updated) : c))
          }
          return next
        })
      } catch (err) {
        const revert = (c) => (c._id === id ? { ...c, [field]: comment[field] || [] } : c)
        setComments((prev) => prev.map(revert))
        throw err
      }
    }
  }, [])

  const toggleLike = useCallback(
    (comment, liked, userId) => toggleMember('likes', likeComment, unlikeComment)(comment, liked, userId),
    [toggleMember]
  )
  const toggleSave = useCallback(
    (comment, saved, userId) => toggleMember('saves', saveComment, unsaveComment)(comment, saved, userId),
    [toggleMember]
  )

  const loadReplies = useCallback(async (parentId) => {
    const replies = await listReplies(parentId)
    setRepliesByParent((prev) => ({ ...prev, [parentId]: replies }))
    return replies
  }, [])

  const addReply = useCallback(async (parentId, { title, text, isPublic, isQuestion, attachments, guides }) => {
    const id = tempId()
    const optimistic = {
      _id: id,
      title: title || '',
      text,
      isPublic: Boolean(isPublic),
      isQuestion: Boolean(isQuestion),
      parent: parentId,
      likes: [],
      saves: [],
      attachments: attachments || [],
      guides: guides || [],
      createdAt: new Date().toISOString(),
      author: { _id: 'me' },
    }
    setRepliesByParent((prev) => ({
      ...prev,
      [parentId]: [...(prev[parentId] || []), optimistic],
    }))
    setComments((prev) =>
      prev.map((c) => (c._id === parentId ? { ...c, replyCount: (c.replyCount || 0) + 1 } : c))
    )
    try {
      const created = await createComment({
        parent: parentId,
        title: title || '',
        text,
        isPublic,
        isQuestion,
        attachments,
        guides: (guides || []).map((g) => g._id),
      })
      setRepliesByParent((prev) => ({
        ...prev,
        [parentId]: (prev[parentId] || []).map((c) => (c._id === id ? created : c)),
      }))
      return created
    } catch (err) {
      setRepliesByParent((prev) => ({
        ...prev,
        [parentId]: (prev[parentId] || []).filter((c) => c._id !== id),
      }))
      setComments((prev) =>
        prev.map((c) =>
          c._id === parentId ? { ...c, replyCount: Math.max(0, (c.replyCount || 1) - 1) } : c
        )
      )
      throw err
    }
  }, [])

  const reload = useCallback(() => {
    if (standard == null) return Promise.resolve([])
    return listComments(standard)
      .then((list) => {
        setComments(list)
        setStatus('ready')
        return list
      })
      .catch(() => [])
  }, [standard])

  return {
    comments,
    status,
    reload,
    add,
    update,
    updateItems,
    updateTopics,
    remove,
    toggleLike,
    toggleSave,
    repliesByParent,
    loadReplies,
    addReply,
  }
}
